import React, { FC } from 'react';
import { Button, Modal } from 'antd';
import { styled } from 'styled-components';
import {
  Icon,
  faCar,
  faCircleInfo,
  faFileCircleExclamation,
} from '@/ui/components';
import { useTranslate } from '@/translations';
import { Damage } from '@/types';
import { getPreTimeCounts } from './PreTimeCell';
import { originalRepairMethod } from './damageLinePresentation';

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 8px;
  border-radius: 8px;
  background: #fff7e6;

  svg {
    font-size: 24px;
    color: #ff9800;
  }
`;

const SummaryTitle = styled.div`
  font-weight: 600;
`;

const Counts = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin: 8px 0 16px;
`;

const CountCard = styled.div`
  padding: 10px 8px;
  border-radius: 8px;
  background: #f5f7fb;
  text-align: center;

  strong {
    display: block;
    font-size: 20px;
  }
`;

const Notice = styled.div`
  margin-top: 8px;
  padding: 8px;
  border-radius: 8px;
  background: #f0f5ff;
  color: #1d39c4;
`;

const FooterActions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  gap: 8px;

  .ant-btn {
    flex: 0 0 auto;
    white-space: nowrap;
  }
`;

export type PtReviewSubmitAction = 'keep' | 'remove';

interface PtReviewRequiredModalProps {
  damage?: Damage;
  open: boolean;
  submitting?: PtReviewSubmitAction;
  onCancel: () => void;
  onKeep: () => void;
  onRemove: () => void;
}

export const PtReviewRequiredModal: FC<PtReviewRequiredModalProps> = ({
  damage,
  open,
  submitting,
  onCancel,
  onKeep,
  onRemove,
}) => {
  const { t } = useTranslate();
  const counts = damage ? getPreTimeCounts(damage) : undefined;
  const previous = damage ? originalRepairMethod(damage) : '';
  const next = damage?.repairActionDesc || damage?.repairActionCode || '';
  const busy = !!submitting;

  return (
    <Modal
      open={open}
      width={720}
      confirmLoading={busy}
      closable={!busy}
      maskClosable={!busy}
      keyboard={!busy}
      title={
        <div className="d-flex align-items-center gap-2">
          <Icon icon={faFileCircleExclamation} color="#d48806" />
          {t('ptReviewRequired')}
        </div>
      }
      onCancel={busy ? undefined : onCancel}
      footer={
        <FooterActions>
          <Button onClick={onCancel} disabled={busy}>
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            onClick={onKeep}
            loading={submitting === 'keep'}
            disabled={busy}
          >
            {t('keepPreTiming')}
          </Button>
          <Button
            danger
            onClick={onRemove}
            loading={submitting === 'remove'}
            disabled={busy}
          >
            {t('removePreTiming')}
          </Button>
        </FooterActions>
      }
    >
      <Summary>
        <Icon icon={faCar} />
        <div>
          <SummaryTitle>{t('customerAmendment')}</SummaryTitle>
          <div>
            {t('repairMethodChange', {
              from: previous || t('notAvailable'),
              to: next || t('notAvailable'),
            })}
          </div>
        </div>
      </Summary>
      <div>{t('currentPreTiming')}</div>
      <Counts>
        <CountCard>
          {t('frCodes')}
          <strong>{counts?.frCount ?? 0}</strong>
        </CountCard>
        <CountCard>
          {t('parts')}
          <strong>{counts?.partsCount ?? 0}</strong>
        </CountCard>
        <CountCard>
          {t('materials')}
          <strong>{counts?.materialsCount ?? 0}</strong>
        </CountCard>
      </Counts>
      <div>{t('isPreTimingCorrect')}</div>
      <Notice>
        <Icon icon={faCircleInfo} /> {t('ptReviewBlocksProgress')}
      </Notice>
    </Modal>
  );
};
